import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Pagination, Space, Spin } from 'antd';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useScenarioScope, type ScenarioScopeToken } from '../pages/marketing/useScenarioScope';

interface CurrentState {
  definition_id: number; key: string; label: string; state: string; version: number;
  entered_at: string; expires_at: string | null; frozen_next_state: string | null;
}
interface HistoryEntry {
  id: number; definition_id: number; key: string; label: string;
  before_state: string | null; after_state: string; source_kind: string;
  version: number; entered_at: string;
}
interface Payload { current: CurrentState[]; history: HistoryEntry[]; total: number }
const positive = (x: unknown) => typeof x === 'number' && Number.isSafeInteger(x) && x > 0;
const timestamp = (x: unknown) => typeof x === 'string' && Number.isFinite(Date.parse(x));
function identified(x: any): boolean {
  return x && positive(x.definition_id) && typeof x.key === 'string' && typeof x.label === 'string'
    && positive(x.version) && timestamp(x.entered_at);
}
function parse(current: unknown, history: unknown): Payload {
  const h = history as { items?: unknown; total?: unknown } | null;
  if (!Array.isArray(current) || !current.every((x) => identified(x) && typeof x.state === 'string'
    && (x.expires_at === null || timestamp(x.expires_at))
    && (x.frozen_next_state === null || typeof x.frozen_next_state === 'string'))
    || !h || !Array.isArray(h.items) || !h.items.every((x) => identified(x) && positive(x.id)
      && (x.before_state === null || typeof x.before_state === 'string') && typeof x.after_state === 'string'
      && ['transition', 'expiry'].includes(x.source_kind))
    || typeof h.total !== 'number' || !Number.isSafeInteger(h.total) || h.total < 0) {
    throw new Error('Unsupported client state response');
  }
  return { current, history: h.items, total: h.total };
}
const time = (value: string) => new Date(value).toLocaleString('ru-RU');

export default function ClientStatesTab({ userId }: { userId: number }) {
  const identity = useAuthStore((s) => [s.tenant?.id, s.admin?.id, s.admin?.role,
    s.isAuthenticated, s.isSwitching].join(':'));
  return <ScopedStates key={identity + ':' + userId} userId={userId} />;
}
function ScopedStates({ userId }: { userId: number }) {
  const scope = useScenarioScope();
  const [page, setPage] = useState(1), [retry, setRetry] = useState(0);
  const [result, setResult] = useState<{
    token: ScenarioScopeToken; page: number; status: 'success' | 'error'; data?: Payload;
  } | null>(null);
  useEffect(() => {
    if (!scope.enabled || !positive(userId)) return;
    const token = scope.capture(), controller = new AbortController();
    let alive = true;
    const abort = () => controller.abort();
    token.controller.signal.addEventListener('abort', abort);
    if (token.controller.signal.aborted) abort();
    const path = '/admin/clients/' + userId + '/states';
    void (async () => {
      try {
        const config = { ...scope.requestConfig(token), signal: controller.signal };
        const [current, history] = await Promise.all([
          api.get(path, config),
          api.get(path + '/history', { ...config, params: { limit: 50, offset: (page - 1) * 50 } }),
        ]);
        if (!alive || controller.signal.aborted || !scope.isCurrent(token)) return;
        const data = parse(current.data, history.data);
        setResult({ token, page, status: 'success', data });
      } catch {
        if (alive && !controller.signal.aborted && scope.isCurrent(token)) setResult({ token, page, status: 'error' });
      }
    })();
    return () => {
      alive = false; controller.abort(); token.controller.signal.removeEventListener('abort', abort);
    };
  }, [scope, userId, page, retry]);
  if (!scope.enabled || !positive(userId)) return <p>Состояния доступны сотрудникам с доступом к клиентам.</p>;
  const live = result && result.page === page && scope.isCurrent(result.token) ? result : null;
  if (!live) return <Spin aria-label="Загрузка состояний" />;
  if (live.status === 'error') return <Space direction="vertical">
    <Alert type="error" message="Не удалось загрузить состояния и историю клиента." />
    <Button onClick={() => { setResult(null); setRetry((x) => x + 1); }}>Повторить загрузку</Button>
  </Space>;
  const data = live.data!;
  return <section aria-label="Состояния клиента">
    <p>Состояния меняются по событиям и срокам. Ручное изменение здесь недоступно.
      {' '}<Link to="/clients/states">Настроить определения состояний</Link></p>
    <h3>Текущие состояния</h3>
    {data.current.length === 0 ? <p>Состояния ещё не назначены.</p> : <table aria-label="Текущие состояния"><thead><tr>
      <th>Определение</th><th>Состояние</th><th>Версия</th><th>Вход</th><th>Переход по сроку</th>
    </tr></thead><tbody>{data.current.map((x) => <tr key={x.definition_id}>
      <td>{x.label} ({x.key})</td><td>{x.state}</td><td>{x.version}</td><td>{time(x.entered_at)}</td>
      <td>{x.expires_at === null ? 'Без срока' : time(x.expires_at) + ' → ' + x.frozen_next_state}</td>
    </tr>)}</tbody></table>}
    <h3>История</h3>
    {data.history.length === 0 ? <p>На этой странице истории нет.</p> : <table aria-label="История состояний"><thead><tr>
      <th>Определение</th><th>Переход</th><th>Причина</th><th>Версия</th><th>Время</th>
    </tr></thead><tbody>{data.history.map((x) => <tr key={x.id}>
      <td>{x.label} ({x.key})</td><td>{x.before_state ?? 'Не назначено'} → {x.after_state}</td>
      <td>{x.source_kind === 'expiry' ? 'Истечение срока' : 'Событие'}</td><td>{x.version}</td><td>{time(x.entered_at)}</td>
    </tr>)}</tbody></table>}
    <Pagination current={page} pageSize={50} total={data.total} showSizeChanger={false} onChange={setPage} />
  </section>;
}
